import { useState, useEffect, useCallback } from "react";
import { CourseMeta } from "../../../types/blog.model";
import { getCourses } from "../../../services/course.service";
import { useLocale } from "../../../context/LocaleContext";

// ── Module-level in-memory cache ─────────────────────────────────────────────
// Keyed by locale so each locale is cached separately.
const CACHE_TTL_MS = 60_000; // 1 minute
const cache = new Map<string, { data: CourseMeta[]; expiresAt: number }>();

export function useCourses() {
  const { locale } = useLocale();
  const cacheKey = locale;

  const getCached = useCallback(() => {
    const entry = cache.get(cacheKey);
    return entry && Date.now() < entry.expiresAt ? entry.data : null;
  }, [cacheKey]);

  const [courses, setCourses] = useState<CourseMeta[]>(() => getCached() ?? []);
  const [loading, setLoading] = useState(() => getCached() === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cached = getCached();
    if (cached) {
      setCourses(cached);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getCourses(locale);

        if (!isMounted) return;

        cache.set(cacheKey, { data, expiresAt: Date.now() + CACHE_TTL_MS });
        setCourses(data);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCourses();

    return () => {
      isMounted = false;
    };
  }, [locale, cacheKey, getCached]);

  return { courses, loading, error };
}
